import React, { useEffect, useState } from 'react';
import logo from '../../assets/images/logo.png';
import auth from '../../assets/images/authentication/img-auth-sideimg.jpg';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { postData } from '../Services/api';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const token = localStorage.getItem("token");

    useEffect(() => {
        if (token) {
            navigate('/');
        }
    }, []);

    const toggleVisibility = () => {
        setShowPassword(!showPassword);
    };

    const initialValues = {
        email: '',
        company_name: '',
        password: ''
    };

    const validationSchema = Yup.object({
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        company_name: Yup.string()
            .required('Company Name is required'),
        password: Yup.string()
            .required('Password is required')
    });

    const [twoFactorPending, setTwoFactorPending] = useState(false);
    const [tempToken, setTempToken] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [verifying2FA, setVerifying2FA] = useState(false);

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const res = await postData('/auth/admin-login', values);

            if (res?.status === true && res?.two_factor_required) {
                setTwoFactorPending(true);
                setTempToken(res.temp_token);
                toast.info("Google 2FA is enabled. Enter your 6-digit Authenticator code.");
                return;
            }

            // ✅ Success check
            if (res?.status === true && res?.data?.token) {
                localStorage.setItem("token", res.data.token);

                toast.success(res.message || "Login successful");

                resetForm();
                const userRole = res.data.user?.role;
                if (userRole === 'accounts') {
                    navigate('/accounts-dashboard/view-edit');
                } else {
                    navigate('/');
                }
            } 
            else {
                toast.error(res?.message || "Login failed");
            }

        } catch (error) {
            console.log("Login Error:", error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Server error. Try again.";

            toast.error(msg);
        } 
        finally {
            setSubmitting(false);
        }
    };

    const handle2FASubmit = async (e) => {
        e.preventDefault();
        if (!otpCode || otpCode.length < 6) {
            toast.error("Please enter a 6-digit code");
            return;
        }

        setVerifying2FA(true);
        try {
            const res = await postData('/auth/2fa/login-verify', {
                temp_token: tempToken,
                code: otpCode
            });

            if (res.status && res.token) {
                localStorage.setItem("token", res.token);
                toast.success("2FA Verification Successful!");
                const userRole = res.data?.role;
                if (userRole === 'accounts') {
                    navigate('/accounts-dashboard/view-edit');
                } else {
                    navigate('/');
                }
            } else {
                toast.error(res.message || "Invalid 2FA Code");
            }
        } catch (error) {
            console.error("2FA verify error:", error);
            toast.error(error.response?.data?.message || "Invalid 2FA Code");
        } finally {
            setVerifying2FA(false);
        }
    };



    return (
        <div className="auth-main">
            <div className="auth-wrapper v2">
                <div className="auth-sidecontent">
                    <img src={auth} alt="images" className="img-fluid img-auth-side" />
                </div>
                <div className="auth-form">
                    <div className="card my-5">
                        <div className="card-body">
                            <div className="text-center mb-5">
                                <Link to="#">
                                    <img src={logo} width={100} height={100} alt="img" />
                                </Link>
                            </div>
                            {twoFactorPending ? (
                                <div>
                                    <div className="text-center mb-3">
                                        <i className="fas fa-shield-alt text-primary display-4 mb-2"></i>
                                        <h4 className="fw-bold">2-Factor Authentication</h4>
                                        <p className="text-muted small">
                                            Enter the 6-digit code generated by your <strong>Google Authenticator</strong> app.
                                        </p>
                                    </div>
                                    <form onSubmit={handle2FASubmit}>
                                        <div className="form-group mb-4">
                                            <input
                                                type="text"
                                                className="form-control form-control-lg text-center letter-spacing-2 fw-bold"
                                                placeholder="000000"
                                                maxLength="6"
                                                value={otpCode}
                                                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                                                required
                                                autoFocus
                                            />
                                        </div>
                                        <div className="d-grid gap-2">
                                            <button type="submit" className="btn btn-primary btn-lg" disabled={verifying2FA}>
                                                {verifying2FA ? (
                                                    <span><i className="fas fa-spinner fa-spin me-2"></i> Verifying...</span>
                                                ) : (
                                                    "Verify & Login"
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm rounded-pill mt-2"
                                                onClick={() => {
                                                    setTwoFactorPending(false);
                                                    setOtpCode('');
                                                }}
                                            >
                                                <i className="fas fa-arrow-left me-1"></i> Back to Email Login
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <>
                                    <h4 className="text-center f-w-500 mb-3">Login with your email</h4>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={handleSubmit}
                                    >
                                        {({ isSubmitting }) => (
                                            <Form>
                                                <div className="form-group mb-3">
                                                    <Field
                                                        type="text"
                                                        name="company_name"
                                                        className="form-control"
                                                        placeholder="Company Name"
                                                    />
                                                    <ErrorMessage
                                                        name="company_name"
                                                        component="div"
                                                        className="text-danger fs-12 mt-1"
                                                    />
                                                </div>
                                                <div className="form-group mb-3">
                                                    <Field
                                                        type="email"
                                                        name="email"
                                                        className="form-control"
                                                        placeholder="Email Address"
                                                    />
                                                    <ErrorMessage
                                                        name="email"
                                                        component="div"
                                                        className="text-danger fs-12 mt-1"
                                                    />
                                                </div>

                                                <div className="input-group">
                                                    <Field
                                                        type={showPassword ? 'text' : 'password'}
                                                        name="password"
                                                        className="form-control"
                                                        placeholder="*******"
                                                    />
                                                    <span
                                                        className="input-group-text"
                                                        onClick={toggleVisibility}
                                                        style={{ cursor: 'pointer' }}
                                                        id="validationTooltipUsernamePrepend"
                                                    >
                                                        <i className={`fa ${!showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                    </span>
                                                </div>
                                                <ErrorMessage
                                                    name="password"
                                                    component="div"
                                                    className="text-danger fs-12 mt-1"
                                                />

                                                <div className="d-grid mt-4">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="spinner-border spinner-border-sm"></span>
                                                        ) : (
                                                            "Sign In"
                                                        )}
                                                    </button>
                                                </div>
                                                <p className="text-center mt-3">
                                                    Register New account? <Link to="/register">Register</Link>
                                                </p>
                                            </Form>
                                        )}
                                    </Formik>
                                </>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
